import React, { useState, useEffect, useRef } from "react";
import { QuizResult } from "../../shared/types";
import CloudPDFExport from "./CloudPDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Radar } from "react-chartjs-2";
import {
  Cloud,
  Speed,
  Analytics,
  Storage,
  AutoFixHigh,
  VpnLock,
  Backup,
  Timeline,
  AttachMoney,
  TrendingUp,
  Assessment,
  Groups,
  Business,
  CloudQueue,
} from "@mui/icons-material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface CloudResultsProps {
  results: QuizResult[];
  onRestart: () => void;
}

const CloudResults: React.FC<CloudResultsProps> = ({ results, onRestart }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("recommendations");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth <= 1024 && window.innerWidth > 768
  );
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width <= 1024 && width > 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Enhanced Cloud recommendations
  const generateCloudRecommendations = () => {
    const recommendations = [];
    const answers = results.map((r) => r.answer.toLowerCase());

    // Analyze user responses
    const hasSecurityNeeds = answers.some(
      (a) =>
        a.includes("security") ||
        a.includes("compliance") ||
        a.includes("gdpr") ||
        a.includes("hipaa")
    );
    const hasScalabilityNeeds = answers.some(
      (a) =>
        a.includes("scalability") || a.includes("50+") || a.includes("growth")
    );
    const hasCostFocus = answers.some(
      (a) => a.includes("cost") || a.includes("$0") || a.includes("reduction")
    );
    const hasCollaborationNeeds = answers.some(
      (a) =>
        a.includes("collaboration") ||
        a.includes("remote") ||
        a.includes("team")
    );
    const hasAutomationNeeds = answers.some(
      (a) =>
        a.includes("automation") ||
        a.includes("full automation") ||
        a.includes("api")
    );
    const isEnterprise = answers.some(
      (a) =>
        a.includes("50+") || a.includes("$1000+") || a.includes("enterprise")
    );

    // Core Infrastructure Recommendations
    recommendations.push({
      category: "Core Infrastructure",
      title: "Cloud Storage & Data Management",
      description:
        "Scalable, secure cloud storage with automated backup, version control, and global accessibility for your business data.",
      priority: "High" as const,
      icon: <Storage />,
      benefits: [
        "99.9% uptime guarantee",
        "Automated backups",
        "Global data replication",
        "Pay-as-you-use pricing",
      ],
      implementation: "3-4 weeks",
      roi: "180% within 3 months",
      score: 95,
      monthlyEstimate: "$50-200",
      provider: "AWS S3 / Azure Blob / Google Cloud Storage",
    });

    if (hasScalabilityNeeds || isEnterprise) {
      recommendations.push({
        category: "Compute & Performance",
        title: "Auto-Scaling Computing Resources",
        description:
          "Dynamic compute resources that automatically scale up or down based on demand, ensuring optimal performance and cost efficiency.",
        priority: "High" as const,
        icon: <Speed />,
        benefits: [
          "Automatic scaling",
          "Load balancing",
          "High availability",
          "Performance optimization",
        ],
        implementation: "4-6 weeks",
        roi: "220% within 4 months",
        score: 92,
        monthlyEstimate: "$100-500",
        provider: "AWS EC2 / Azure VMs / Google Compute Engine",
      });
    }

    if (hasSecurityNeeds) {
      recommendations.push({
        category: "Security & Compliance",
        title: "Advanced Security & Compliance Suite",
        description:
          "Enterprise-grade security with encryption, identity management, compliance monitoring, and threat detection.",
        priority: "High" as const,
        icon: <VpnLock />,
        benefits: [
          "End-to-end encryption",
          "Identity & access management",
          "Compliance automation",
          "Real-time threat detection",
        ],
        implementation: "6-8 weeks",
        roi: "300% within 6 months",
        score: 90,
        monthlyEstimate: "$200-800",
        provider: "AWS Security Hub / Azure Security Center",
      });
    }

    if (hasCollaborationNeeds) {
      recommendations.push({
        category: "Collaboration & Productivity",
        title: "Cloud Collaboration Platform",
        description:
          "Integrated suite of productivity tools including file sharing, real-time collaboration, and communication tools.",
        priority: "Medium" as const,
        icon: <Groups />,
        benefits: [
          "Real-time collaboration",
          "Document sharing",
          "Video conferencing",
          "Mobile accessibility",
        ],
        implementation: "3-4 weeks",
        roi: "150% within 2 months",
        score: 88,
        monthlyEstimate: "$10-50 per user",
        provider: "Microsoft 365 / Google Workspace",
      });
    }

    if (hasAutomationNeeds) {
      recommendations.push({
        category: "Automation & Integration",
        title: "Workflow Automation & API Management",
        description:
          "Automated workflows, API management, and integration platform to streamline business processes.",
        priority: "Medium" as const,
        icon: <AutoFixHigh />,
        benefits: [
          "Process automation",
          "API management",
          "Third-party integrations",
          "Workflow orchestration",
        ],
        implementation: "8-12 weeks",
        roi: "250% within 8 months",
        score: 85,
        monthlyEstimate: "$100-300",
        provider: "AWS Lambda / Azure Functions / Zapier",
      });
    }

    // Analytics & Monitoring
    recommendations.push({
      category: "Analytics & Monitoring",
      title: "Cloud Analytics & Business Intelligence",
      description:
        "Real-time analytics, monitoring dashboards, and business intelligence tools to drive data-driven decisions.",
      priority: "Medium" as const,
      icon: <Analytics />,
      benefits: [
        "Real-time dashboards",
        "Predictive analytics",
        "Custom reporting",
        "Performance monitoring",
      ],
      implementation: "6-10 weeks",
      roi: "200% within 6 months",
      score: 82,
      monthlyEstimate: "$50-250",
      provider: "Power BI / Tableau / Google Analytics",
    });

    // Backup & Disaster Recovery
    recommendations.push({
      category: "Business Continuity",
      title: "Disaster Recovery & Business Continuity",
      description:
        "Comprehensive backup and disaster recovery solution ensuring business continuity with minimal downtime.",
      priority: hasCostFocus ? "Low" : "Medium",
      icon: <Backup />,
      benefits: [
        "Automated backups",
        "Point-in-time recovery",
        "Geo-redundancy",
        "RTO/RPO compliance",
      ],
      implementation: "4-6 weeks",
      roi: "400% during incidents",
      score: 78,
      monthlyEstimate: "$30-150",
      provider: "AWS Backup / Azure Backup",
    });

    // Cost Optimization
    if (hasCostFocus) {
      recommendations.push({
        category: "Cost Optimization",
        title: "Cloud Cost Management & Optimization",
        description:
          "AI-powered cost optimization tools with automated resource management and spending alerts.",
        priority: "High" as const,
        icon: <AttachMoney />,
        benefits: [
          "Cost optimization AI",
          "Resource right-sizing",
          "Spending alerts",
          "Budget management",
        ],
        implementation: "3-4 weeks",
        roi: "150% in cost savings",
        score: 87,
        monthlyEstimate: "$20-100",
        provider: "AWS Cost Explorer / Azure Cost Management",
      });
    }

    return recommendations;
  };

  const recommendations = generateCloudRecommendations();
  const categories = [...new Set(recommendations.map((r) => r.category))];

  // Responsive helper functions
  const getFontSize = (mobile: string, tablet: string, desktop: string) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const getSpacing = (mobile: string, tablet: string, desktop: string) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  // Chart data
  const categoryData = {
    labels: categories,
    datasets: [
      {
        label: "Number of Cloud Solutions",
        data: categories.map(
          (cat) => recommendations.filter((r) => r.category === cat).length
        ),
        backgroundColor: "rgba(99, 102, 241, 0.8)",
        borderColor: "#667eea",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const roiBasedPriorityData = {
    labels: [
      "Immediate Impact (High ROI)",
      "Strategic Growth (Medium ROI)",
      "Future Enhancement (Lower ROI)",
    ],
    datasets: [
      {
        data: [
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 200;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 150 && roiValue < 200;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue < 150;
          }).length,
        ],
        backgroundColor: ["#667eea", "#f59e0b", "#10b981"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  const roiImplementationData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Expected ROI (%)",
        data: recommendations.map((r) => {
          const roiMatch = r.roi.match(/(\d+)%/);
          return roiMatch ? parseInt(roiMatch[1]) : 100;
        }),
        backgroundColor: "rgba(245, 158, 11, 0.6)",
        borderColor: "#f59e0b",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Implementation Score",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.6)",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Implementation timeline data
  const implementationTimelineData = {
    labels: [
      "Quick Win (1-2 weeks)",
      "Medium Effort (2-4 weeks)",
      "Strategic Project (4+ weeks)",
    ],
    datasets: [
      {
        data: [
          recommendations.filter((r) => r.implementation.includes("1-2"))
            .length,
          recommendations.filter(
            (r) =>
              r.implementation.includes("2-3") ||
              r.implementation.includes("3-4") ||
              r.implementation.includes("2-4")
          ).length,
          recommendations.filter(
            (r) =>
              r.implementation.includes("4-") ||
              r.implementation.includes("5-") ||
              r.implementation.includes("6")
          ).length,
        ],
        backgroundColor: ["#10b981", "#f59e0b", "#667eea"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  // Business impact data
  const businessImpactData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Business Impact Score (0-100)",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(103, 126, 234, 0.3)",
        borderColor: "#667eea",
        pointBackgroundColor: "#667eea",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#667eea",
      },
    ],
  };

  // Updated options for charts to match CRM style
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: isMobile ? 10 : 15,
          font: {
            size: isMobile ? 8 : 10,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || "";
            const value = context.parsed.y || context.parsed || context.raw;
            return `${label ? label + ": " : ""}${value}${
              label.includes("ROI") ? "%" : ""
            }`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: !isMobile,
          text: "Number of Solutions",
        },
        ticks: {
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
      x: {
        ticks: {
          font: {
            size: isMobile ? 8 : 10,
          },
          maxRotation: isMobile ? 45 : 0,
        },
      },
    },
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: getSpacing("0.75rem", "1.5rem", "2rem"),
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto", width: "100%" }}>
        {/* Header */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: getSpacing("1.5rem", "2.5rem", "3rem"),
            color: "white",
            padding: isMobile ? "0 0.5rem" : "0",
          }}
        >
          <div
            style={{
              fontSize: getFontSize("2rem", "3rem", "4rem"),
              marginBottom: "1rem",
            }}
          >
            <Cloud
              sx={{
                fontSize: getFontSize("2rem", "3rem", "4rem"),
                color: "white",
              }}
            />
          </div>
          <h1
            style={{
              fontSize: getFontSize("1.75rem", "2.5rem", "3rem"),
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(45deg, #ffffff 0%, #e0e7ff 50%, #c7d2fe 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
            }}
          >
            Your Cloud Solutions Roadmap
          </h1>
          <p
            style={{
              fontSize: getFontSize("0.9rem", "1rem", "1.1rem"),
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
            }}
          >
            Personalized cloud architecture recommendations based on your
            business needs, budget, and growth objectives with detailed
            implementation guidance.
          </p>

          {/* Key Statistics */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : isTablet
                ? "repeat(3, 1fr)"
                : "repeat(auto-fit, minmax(180px, 1fr))",
              gap: getSpacing("0.5rem", "0.75rem", "1rem"),
              maxWidth: isMobile ? "100%" : "800px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: getSpacing("0.75rem", "1rem", "1.5rem"),
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <CloudQueue
                sx={{
                  fontSize: getFontSize("1.25rem", "1.5rem", "2rem"),
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: getFontSize("1rem", "1.25rem", "1.5rem"),
                  fontWeight: "bold",
                }}
              >
                {recommendations.length}
              </div>
              <div
                style={{
                  fontSize: getFontSize("0.7rem", "0.8rem", "0.9rem"),
                  opacity: 0.8,
                }}
              >
                Cloud Solutions
              </div>
            </div>

            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: getSpacing("0.75rem", "1rem", "1.5rem"),
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Timeline
                sx={{
                  fontSize: getFontSize("1.25rem", "1.5rem", "2rem"),
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: getFontSize("1rem", "1.25rem", "1.5rem"),
                  fontWeight: "bold",
                }}
              >
                3-12 weeks
              </div>
              <div
                style={{
                  fontSize: getFontSize("0.7rem", "0.8rem", "0.9rem"),
                  opacity: 0.8,
                }}
              >
                Implementation
              </div>
            </div>

            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: getSpacing("0.75rem", "1rem", "1.5rem"),
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                gridColumn: isMobile ? "1 / -1" : "auto",
              }}
            >
              <AttachMoney
                sx={{
                  fontSize: getFontSize("1.25rem", "1.5rem", "2rem"),
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: getFontSize("1rem", "1.25rem", "1.5rem"),
                  fontWeight: "bold",
                }}
              >
                200%+
              </div>
              <div
                style={{
                  fontSize: getFontSize("0.7rem", "0.8rem", "0.9rem"),
                  opacity: 0.8,
                }}
              >
                Expected ROI
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: getSpacing("1.5rem", "2rem", "3rem"),
            flexWrap: "wrap",
            gap: getSpacing("0.25rem", "0.5rem", "0.5rem"),
          }}
        >
          {["recommendations", "analytics", "your-responses"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: getSpacing(
                  "0.5rem 0.75rem",
                  "0.75rem 1rem",
                  "1rem 2rem"
                ),
                backgroundColor:
                  selectedTab === tab
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.1)",
                color: selectedTab === tab ? "#1f2937" : "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: getFontSize("0.75rem", "0.9rem", "1.1rem"),
                fontWeight: "600",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                textTransform: "capitalize",
                whiteSpace: "nowrap",
              }}
            >
              {tab === "your-responses" ? "Your Responses" : tab}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        {selectedTab === "recommendations" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Recommendations Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                  ? "repeat(auto-fit, minmax(350px, 1fr))"
                  : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: getSpacing("1rem", "1.5rem", "2rem"),
                marginBottom: "3rem",
              }}
            >
              {recommendations.map((rec, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: getSpacing("1rem", "1.5rem", "2.5rem"),
                    backdropFilter: "blur(10px)",
                    transition: "transform 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(-8px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {/* Priority Badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      fontWeight: "600",
                      backgroundColor:
                        rec.priority === "High"
                          ? "#fee2e2"
                          : rec.priority === "Medium"
                          ? "#fef3c7"
                          : "#d1fae5",
                      color:
                        rec.priority === "High"
                          ? "#dc2626"
                          : rec.priority === "Medium"
                          ? "#d97706"
                          : "#059669",
                    }}
                  >
                    {rec.priority} Priority
                  </div>

                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: getFontSize("1.5rem", "2rem", "3rem"),
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "0.75rem" : "0",
                        color: "#c9a750ff", // Changed from #667eea to #c9a750ff
                      }}
                    >
                      {React.cloneElement(rec.icon, {
                        sx: { fontSize: getFontSize("1.5rem", "2rem", "3rem") },
                      })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: getFontSize("0.7rem", "0.8rem", "0.9rem"),
                          color: "#c9a750ff", // Changed from #667eea to #c9a750ff
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {rec.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: getFontSize("1rem", "1.2rem", "1.4rem"),
                          lineHeight: "1.3",
                        }}
                      >
                        {rec.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: getFontSize("0.8rem", "0.9rem", "1rem"),
                    }}
                  >
                    {rec.description}
                  </p>

                  {/* Benefits */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: getFontSize("0.9rem", "1rem", "1rem"),
                      }}
                    >
                      Key Benefits:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(2, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {rec.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: getFontSize("0.7rem", "0.8rem", "0.9rem"),
                            color: "#4b5563",
                          }}
                        >
                          <span
                            style={{ color: "#10b981", marginRight: "0.5rem" }}
                          >
                            ✓
                          </span>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Details */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                      gap: "1rem",
                      padding: "1rem",
                      backgroundColor: "rgba(103, 126, 234, 0.1)",
                      borderRadius: "12px",
                      marginBottom: "1rem",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Timeline
                      </div>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {rec.implementation}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Expected ROI
                      </div>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#059669",
                        }}
                      >
                        {rec.roi}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        gridColumn: isMobile ? "1 / -1" : "auto",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Monthly Cost
                      </div>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#c9a750ff", // Changed from #667eea
                        }}
                      >
                        {rec.monthlyEstimate}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        gridColumn: isMobile ? "1 / -1" : "auto",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Recommended Provider
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          color: "#c9a750ff", // Changed from #667eea
                        }}
                      >
                        {rec.provider}
                      </div>
                    </div>
                  </div>

                  {/* Impact Score */}
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#6b7280",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Business Impact Score
                    </div>
                    <div
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#c9a750ff", // Primary color
                        background:
                          "linear-gradient(45deg, #c9a750ff, #e5c879)", // Changed from blue gradient
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {rec.score}/100
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {selectedTab === "analytics" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Charts Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                  ? "1fr"
                  : "repeat(2, 1fr)",
                gap: getSpacing("1rem", "1.5rem", "2rem"),
                marginBottom: "3rem",
              }}
            >
              {/* Implementation Timeline Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: getSpacing("1rem", "1.5rem", "2rem"),
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontSize: getFontSize("1rem", "1.1rem", "1.3rem"),
                    fontWeight: "600",
                  }}
                >
                  <Timeline
                    sx={{ marginRight: "0.5rem", color: "#c9a750ff" }}
                  />{" "}
                  {/* Changed from #667eea to #c9a750ff */}
                  Implementation Timeline
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                  }}
                >
                  Distribution of solution deployment timelines
                </p>
                <div style={{ height: isMobile ? "250px" : "300px" }}>
                  <Doughnut
                    data={implementationTimelineData}
                    options={chartOptions}
                  />
                </div>
              </div>

              {/* ROI-Based Priority Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: getSpacing("1rem", "1.5rem", "2rem"),
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontSize: getFontSize("1rem", "1.1rem", "1.3rem"),
                    fontWeight: "600",
                  }}
                >
                  <TrendingUp
                    sx={{ marginRight: "0.5rem", color: "#c9a750ff" }} // Changed from #667eea to #c9a750ff
                  />
                  ROI-Based Priority
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                  }}
                >
                  Solution prioritization based on expected return on investment
                </p>
                <div style={{ height: isMobile ? "250px" : "300px" }}>
                  <Doughnut
                    data={roiBasedPriorityData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: "bottom" as const,
                          labels: {
                            usePointStyle: true,
                            padding: isMobile ? 10 : 20,
                            font: {
                              size: isMobile ? 10 : 12,
                            },
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context: any) {
                              return `${context.label}: ${context.parsed} solutions`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Solutions by Business Area */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: getSpacing("1rem", "1.5rem", "2rem"),
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontSize: getFontSize("1rem", "1.1rem", "1.3rem"),
                    fontWeight: "600",
                  }}
                >
                  <Assessment
                    sx={{ marginRight: "0.5rem", color: "#c9a750ff" }} // Changed from #667eea to #c9a750ff
                  />
                  Solutions by Business Area
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                  }}
                >
                  Distribution of cloud solutions across different business
                  functions
                </p>
                <div style={{ height: isMobile ? "250px" : "300px" }}>
                  <Bar
                    data={categoryData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function (context: any) {
                              return `${context.parsed.y} recommended solutions`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: !isMobile,
                            text: "Number of Solutions",
                          },
                          ticks: {
                            font: {
                              size: isMobile ? 10 : 12,
                            },
                          },
                        },
                        x: {
                          ticks: {
                            font: {
                              size: isMobile ? 8 : 10,
                            },
                            maxRotation: isMobile ? 45 : 0,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Business Impact Score */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: getSpacing("1rem", "1.5rem", "2rem"),
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontSize: getFontSize("1rem", "1.1rem", "1.3rem"),
                    fontWeight: "600",
                  }}
                >
                  <Business
                    sx={{ marginRight: "0.5rem", color: "#c9a750ff" }} // Changed from #667eea to #c9a750ff
                  />
                  Business Impact Score
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                  }}
                >
                  Potential business impact of each recommended solution (score
                  out of 100)
                </p>
                <div style={{ height: isMobile ? "250px" : "300px" }}>
                  <Radar
                    data={businessImpactData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function (context: any) {
                              return `Impact Score: ${context.parsed.r}/100`;
                            },
                          },
                        },
                      },
                      scales: {
                        r: {
                          beginAtZero: true,
                          max: 100,
                          ticks: {
                            stepSize: 20,
                            font: {
                              size: isMobile ? 8 : 10,
                            },
                          },
                          pointLabels: {
                            font: {
                              size: isMobile ? 8 : 10,
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* ROI vs Implementation Score */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: getSpacing("1rem", "1.5rem", "2rem"),
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontSize: getFontSize("1rem", "1.1rem", "1.3rem"),
                    fontWeight: "600",
                  }}
                >
                  <Timeline
                    sx={{ marginRight: "0.5rem", color: "#c9a750ff" }} // Changed from #667eea to #c9a750ff
                  />
                  ROI vs Implementation Score
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                  }}
                >
                  Expected ROI and implementation difficulty for your
                  recommended solutions
                </p>
                <div style={{ height: isMobile ? "250px" : "300px" }}>
                  <Bar
                    data={roiImplementationData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: "bottom" as const,
                          labels: {
                            usePointStyle: true,
                            padding: isMobile ? 10 : 15,
                            font: {
                              size: isMobile ? 8 : 10,
                            },
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context: any) {
                              const label = context.dataset.label;
                              const value = context.parsed.y;
                              return `${label}: ${value}${
                                label.includes("ROI") ? "%" : "/100"
                              }`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 200,
                          title: {
                            display: !isMobile,
                            text: "Percentage / Score",
                          },
                          ticks: {
                            font: {
                              size: isMobile ? 8 : 10,
                            },
                          },
                        },
                        x: {
                          ticks: {
                            font: {
                              size: isMobile ? 8 : 10,
                            },
                            maxRotation: isMobile ? 45 : 0,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Your Responses Tab */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: getSpacing("1rem", "1.5rem", "2.5rem"),
                marginBottom: "2rem",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Response Summary */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                  padding: "1.5rem",
                  backgroundColor: "rgba(103, 126, 234, 0.1)",
                  borderRadius: "12px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", color: "#667eea" }}>📋</div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {results.length}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
                    Questions Answered
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", color: "#10b981" }}>⚡</div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {
                      recommendations.filter((r) => r.priority === "High")
                        .length
                    }
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
                    High Priority
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", color: "#f59e0b" }}>🎯</div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {Math.round(
                      recommendations.reduce((acc, r) => acc + r.score, 0) /
                        recommendations.length
                    )}
                    %
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
                    Avg Match Score
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", color: "#8b5cf6" }}>☁️</div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {categories.length}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
                    Solution Areas
                  </div>
                </div>
              </div>

              {/* Individual Responses */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "1.5rem",
                }}
              >
                {results.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "2rem",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "16px",
                      border: "1px solid rgba(103, 126, 234, 0.2)",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "1.5rem",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#c9a750ff", // Changed from #667eea to #c9a750ff (Inthera gold)
                          color: "white",
                          borderRadius: "50%",
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.9rem",
                          fontWeight: "600",
                          marginRight: isMobile ? "0" : "1rem",
                          marginBottom: isMobile ? "1rem" : "0",
                          alignSelf: isMobile ? "center" : "flex-start",
                        }}
                      >
                        Q{index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5
                          style={{
                            fontWeight: "700",
                            color: "#1f2937",
                            marginBottom: "0.5rem",
                            fontSize: "1.1rem",
                            lineHeight: "1.4",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {result.question}
                        </h5>
                      </div>
                    </div>

                    <div
                      style={{
                        backgroundColor: "rgba(103, 126, 234, 0.1)",
                        padding: "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #c9a750ff", // Changed from #667eea
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Your Response:
                      </div>
                      <div
                        style={{
                          color: "#c9a750ff", // Changed from #667eea
                          fontWeight: "600",
                          fontSize: "1rem",
                        }}
                      >
                        {result.answer}
                      </div>
                    </div>

                    <div
                      style={{
                        backgroundColor: "rgba(16, 185, 129, 0.05)",
                        padding: "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #10b981",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Impact on Recommendations:
                      </div>
                      <div
                        style={{
                          color: "#059669",
                          fontSize: "0.95rem",
                          lineHeight: "1.5",
                        }}
                      >
                        {/* Dynamic impact analysis */}
                        {result.answer.toLowerCase().includes("security") ||
                        result.answer.toLowerCase().includes("compliance")
                          ? "✓ Enhanced security and compliance features prioritized in your recommendations"
                          : result.answer
                              .toLowerCase()
                              .includes("scalability") ||
                            result.answer.toLowerCase().includes("50+")
                          ? "✓ Auto-scaling and enterprise-grade solutions included for growth capacity"
                          : result.answer.toLowerCase().includes("cost") ||
                            result.answer.toLowerCase().includes("$0")
                          ? "✓ Cost-optimization tools and budget-friendly solutions prioritized"
                          : result.answer
                              .toLowerCase()
                              .includes("collaboration")
                          ? "✓ Team collaboration and productivity tools emphasized in your cloud strategy"
                          : result.answer.toLowerCase().includes("automation")
                          ? "✓ Advanced automation and workflow solutions integrated into recommendations"
                          : "✓ Influenced your personalized cloud architecture and solution prioritization"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "0.75rem" : "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <CloudPDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
              height: "60px", // Added fixed height to match Export button
              display: "flex", // Added to center text vertically
              alignItems: "center", // Added to center text vertically
              justifyContent: "center", // Added to center text horizontally
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloudResults;
